import React, { useState, useCallback } from "react";
import { View, StyleSheet, Platform, Image as RNImage } from "react-native";
import CustomText from "../../common/general/CustomText/CustomText";
import { Image } from "@phosphor-icons/react/dist/ssr/Image";
import useFileUpload, { generatePublicUrl } from "../../../hooks/useFileUpload";
import { StoragePathType } from "@axio/graphql/generated/graphql";
import { useAppConfig } from "../../../AppConfigProvider";

interface CourseThumbnailUploadProps {
  value?: string;
  onChange: (uri: string, filePath: string) => void;
  onError?: (error: string) => void;
}

const CourseThumbnailUpload: React.FC<CourseThumbnailUploadProps> = ({ value, onChange, onError }) => {
  const { currentDomain } = useAppConfig();
  const [isDragging, setIsDragging] = useState(false);

  const { handleServerUpload } = useFileUpload({
    storagePath: StoragePathType.Thumbnails,
    onError: onError,
    isPublic: true,
  });

  const handleFileChange = useCallback(
    async (uploadingFile: File) => {
      let uri = null;

      const reader = new FileReader();
      reader.onloadend = async () => {
        const result = reader.result as string;
        uri = result;

        const { file, signedUrl } = await handleServerUpload({
          uri,
          mimeType: uploadingFile.type,
          size: uploadingFile.size,
          name: uploadingFile.name,
        });

        const publicUrl = generatePublicUrl(currentDomain, file.filePath);

        onChange(uri, publicUrl);
      };
      reader.readAsDataURL(uploadingFile);
    },
    [onChange, onError, handleServerUpload]
  );

  // Mobile version
  if (Platform.OS !== "web") {
    return (
      <View style={styles.mobileContainer}>
        {value ? (
          <RNImage source={{ uri: value }} style={styles.mobileImage} resizeMode="cover" />
        ) : (
          <View style={styles.mobilePlaceholder}>
            <Image size={48} color="#9CA3AF" />
            <CustomText
              text="No thumbnail uploaded"
              size="s"
              style={styles.placeholderText}
              useTranslationText={false}
            />
          </View>
        )}
      </View>
    );
  }

  // Web version
  const containerStyle: React.CSSProperties = {
    width: 300,
    height: 169, // 16:9 aspect ratio
    borderRadius: 8,
    borderWidth: 2,
    borderStyle: "dashed",
    borderColor: isDragging ? "#3E68FE" : "#D1D5DB",
    backgroundColor: isDragging ? "#EEF2FF" : "#F9FAFB",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    overflow: "hidden",
    // Remove cursor pointer since we don't want the container to be clickable
  };

  // Only allow drag and drop, no click on the container itself
  const handleDragEvents = {
    onDragEnter: (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(true);
    },
    onDragLeave: (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
    },
    onDragOver: (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(true);
    },
    onDrop: (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const file = e.dataTransfer?.files?.[0];
      if (file) {
        handleFileChange(file);
      }
    },
  };

  return (
    <div style={containerStyle} {...handleDragEvents}>
      {value ? (
        <img
          src={value}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            borderRadius: 6,
          }}
          alt="Course thumbnail"
        />
      ) : (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
          }}>
          <Image size={48} color="#9CA3AF" />
          <CustomText
            text="Drag & drop or use Upload button"
            size="s"
            style={{
              color: "#9CA3AF",
              fontSize: 14,
              fontWeight: "500",
              textAlign: "center",
            }}
            useTranslationText={false}
          />
          <CustomText
            text="JPG or PNG format, Max 2MB"
            size="xs"
            style={{
              color: "#9CA3AF",
              fontSize: 12,
              textAlign: "center",
            }}
            useTranslationText={false}
          />
        </div>
      )}
    </div>
  );
};

const styles = StyleSheet.create({
  mobileContainer: {
    width: 300,
    height: 169,
    borderRadius: 8,
    borderWidth: 2,
    borderStyle: "dashed",
    borderColor: "#D1D5DB",
    backgroundColor: "#F9FAFB",
    justifyContent: "center",
    alignItems: "center",
    overflow: "hidden",
  },
  mobileImage: {
    width: "100%",
    height: "100%",
    borderRadius: 6,
  },
  mobilePlaceholder: {
    justifyContent: "center",
    alignItems: "center",
    gap: 8,
  },
  placeholderText: {
    color: "#9CA3AF",
    fontSize: 14,
    fontWeight: "500",
    textAlign: "center",
  },
});

export default CourseThumbnailUpload;
